import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getMilestonesByProjectId,
  addMilestone,
  updateMilestone,
  deleteMilestone, // ✅ assumed existing
} from "../../services/milestoneService";

const MilestoneManager = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  const [milestones, setMilestones] = useState([]);
  const [editingItem, setEditingItem] = useState(null);

  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [error, setError] = useState("");

  const fetchMilestones = useCallback(async () => {
    try {
      const data = await getMilestonesByProjectId(projectId);
      setMilestones(data);
    } catch {
      setError("Failed to load milestones.");
    }
  }, [projectId]);

  useEffect(() => {
    fetchMilestones();
  }, [fetchMilestones]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      title,
      projectId,
      amount: parseFloat(amount),
      note,
      dueDate,
    };

    try {
      if (editingItem) {
        await updateMilestone({ ...payload, id: editingItem.id });
      } else {
        await addMilestone(payload);
      }

      setTitle("");
      setAmount("");
      setNote("");
      setDueDate("");
      setEditingItem(null);

      fetchMilestones();
    } catch {
      setError("Failed to save milestone.");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setTitle(item.title);
    setAmount(item.amount);
    setNote(item.note);
    setDueDate(item.dueDate);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this milestone?"))
      return;

    try {
      await deleteMilestone(id);
      fetchMilestones();
    } catch {
      alert("Failed to delete milestone.");
    }
  };

  return (
    <div className="p-0 md:p-4">
      {/* ================= HEADER ================= */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">
          Milestones for Project #{projectId}
        </h2>

        <button
          onClick={() => navigate(-1)}
          className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
        >
          ← Back
        </button>
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}

      {/* ================= FORM ================= */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Milestone Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="w-full px-3 py-2 border rounded"
          />

          <input
            type="number"
            placeholder="Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            className="w-full px-3 py-2 border rounded"
          />

          <textarea
            placeholder="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            required
            rows={3}
            className="w-full px-3 py-2 border rounded"
          />

          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          />

          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
          >
            {editingItem ? "Update Milestone" : "Add Milestone"}
          </button>
        </form>
      </div>

      {/* ================= MOBILE VIEW ================= */}
      <div className="md:hidden space-y-4">
        {milestones.map((m) => (
          <div
            key={m.id}
            className="bg-white rounded-xl shadow-sm p-4 border space-y-2"
          >
            <div className="flex justify-between items-start">
              <h3 className="font-semibold">{m.title}</h3>
              <span className="text-sm font-medium text-green-700">
                ₹{m.amount}
              </span>
            </div>

            <p className="text-sm text-gray-600">{m.note}</p>

            <p className="text-xs text-gray-500">
              Due: {m.dueDate || "—"}
            </p>

            <div className="flex justify-end gap-4 pt-2">
              <button
                onClick={() => handleEdit(m)}
                className="text-blue-600 text-sm hover:underline"
              >
                Edit
              </button>

              <button
                onClick={() => handleDelete(m.id)}
                className="text-red-600 text-sm hover:underline"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ================= DESKTOP VIEW ================= */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full bg-white rounded shadow text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="p-2 text-left">Title</th>
              <th className="p-2 text-left">Amount</th>
              <th className="p-2 text-left">Note</th>
              <th className="p-2 text-left">Due Date</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {milestones.map((m) => (
              <tr key={m.id} className="border-t hover:bg-gray-50">
                <td className="p-2">{m.title}</td>
                <td className="p-2">₹{m.amount}</td>
                <td className="p-2">{m.note}</td>
                <td className="p-2">{m.dueDate || "-"}</td>
                <td className="p-2 flex gap-3">
                  <button
                    onClick={() => handleEdit(m)}
                    className="text-blue-600 hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(m.id)}
                    className="text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MilestoneManager;
